import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type Account, RequestNotificationEmailChangeInputSchema } from '@terafile/shared'
import { type FormEvent, useState } from 'react'
import {
  ACCOUNT_QUERY_KEY,
  requestNotificationEmailChange,
  useAccount,
} from '../../lib/account-api.js'
import { useAuth } from '../../lib/auth-context.js'
import { FETCH_ERROR_FALLBACK, SUBMIT_ERROR_FALLBACK, toUserMessage } from '../../lib/error-copy.js'
import { LOADING } from '../../lib/labels.js'
import { useToast } from '../../lib/toast.js'
import { SettingsSectionCard, SettingsUnavailableNotice } from './SettingsSectionCard.js'

const TITLE = '通知先メールアドレス'
const DESCRIPTION =
  'お休み状態の前のお知らせや、共有URLからファイルが届いたときのお知らせを受け取るメールアドレスです。ログインには使いません。'

export function SettingsNotificationEmailSection() {
  const { session } = useAuth()
  const accountQ = useAccount({ enabled: session !== null })

  if (!session) {
    return (
      <SettingsSectionCard title={TITLE} description={DESCRIPTION}>
        <SettingsUnavailableNotice message="アカウント登録後に利用できます。" />
      </SettingsSectionCard>
    )
  }

  return (
    <SettingsSectionCard title={TITLE} description={DESCRIPTION}>
      {accountQ.isLoading && <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>}
      {accountQ.error && (
        <p className="text-xs text-destructive">
          {toUserMessage(accountQ.error, FETCH_ERROR_FALLBACK)}
        </p>
      )}
      {accountQ.data && <NotificationEmailForm account={accountQ.data} />}
    </SettingsSectionCard>
  )
}

function NotificationEmailForm({ account }: { account: Account }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: requestNotificationEmailChange,
    onSuccess: (updated) => {
      queryClient.setQueryData(ACCOUNT_QUERY_KEY, updated)
      setValue('')
      toast.success(
        updated.pendingNotificationEmail
          ? '確認メールを送りました。メール内のリンクを開くと切り替わります'
          : '通知先をログイン用メールアドレスに戻しました',
      )
    },
    onError: (err) => setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK)),
  })

  const submit = (email: string) => {
    setError(null)
    const parsed = RequestNotificationEmailChangeInputSchema.safeParse({ email })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? SUBMIT_ERROR_FALLBACK)
      return
    }
    mutation.mutate(parsed.data.email)
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit(value)
  }

  return (
    <div className="space-y-4">
      <CurrentAddress
        account={account}
        isPending={mutation.isPending}
        onResetToLogin={() => submit(account.loginEmail)}
      />
      {account.pendingNotificationEmail && (
        <PendingNotice pending={account.pendingNotificationEmail} />
      )}
      <NewAddressForm
        value={value}
        onChange={setValue}
        onSubmit={onSubmit}
        error={error}
        isPending={mutation.isPending}
      />
    </div>
  )
}

type CurrentAddressProps = {
  account: Account
  isPending: boolean
  onResetToLogin: () => void
}

function CurrentAddress({ account, isPending, onResetToLogin }: CurrentAddressProps) {
  const isSameAsLogin = account.notificationEmail === null
  return (
    <div>
      <p className="text-sm font-medium mb-1">いまの通知先</p>
      <p className="text-sm text-muted-foreground break-all">
        {account.notificationEmail ?? account.loginEmail}
        {isSameAsLogin && <span className="ml-1.5 text-xs">（ログイン用と同じ）</span>}
      </p>
      {!isSameAsLogin && (
        <button
          type="button"
          onClick={onResetToLogin}
          disabled={isPending}
          className="mt-1 text-xs text-primary underline-offset-2 hover:underline disabled:opacity-40"
        >
          ログイン用メールアドレス（{account.loginEmail}）に戻す
        </button>
      )}
    </div>
  )
}

type NewAddressFormProps = {
  value: string
  onChange: (value: string) => void
  onSubmit: (e: FormEvent) => void
  error: string | null
  isPending: boolean
}

function NewAddressForm({ value, onChange, onSubmit, error, isPending }: NewAddressFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div>
        <label htmlFor="settings-notification-email" className="block text-sm font-medium mb-1">
          新しい通知先
        </label>
        <input
          id="settings-notification-email"
          type="email"
          autoComplete="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error !== null}
          aria-describedby="settings-notification-email-hint"
          className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
        />
        <p id="settings-notification-email-hint" className="text-xs text-muted-foreground mt-1">
          入力したメールアドレスに確認メールが届きます。リンクを開くと切り替わり、それまではいまの通知先に届きます。
        </p>
      </div>
      {error && (
        <p
          role="alert"
          className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          type="submit"
          disabled={isPending || value.trim() === ''}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          {isPending ? LOADING.saving : '確認メールを送る'}
        </button>
      </div>
    </form>
  )
}

function PendingNotice({ pending }: { pending: string }) {
  return (
    <div className="text-xs border border-amber-300 bg-amber-50 text-amber-900 rounded-md px-3 py-2">
      <span className="font-medium break-all">{pending}</span>{' '}
      に確認メールを送りました。メール内のリンクを開くと切り替わります（有効期限は24時間）。届かない場合は、もう一度送ってください。
    </div>
  )
}
